import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import { getDataLifecycleEvents } from '../api/dataLifecycleApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';

const { semantic } = colorTokens;

const ENTITY_FILTERS = ['customers', 'tenants', 'support_tickets'];

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function eventTypeTone(eventType) {
  const normalized = String(eventType || '').toLowerCase();
  if (normalized.includes('restore')) return 'Success';
  if (normalized.includes('purge') || normalized.includes('delete')) return 'Danger';
  if (normalized.includes('hold') || normalized.includes('request')) return 'Warning';
  return 'Neutral';
}

function formatDate(value, emptyValue) {
  if (!value) return emptyValue;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export function DataLifecycleEventsPage() {
  const { t } = useTranslation(['dataLifecycle', 'common']);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entityType, setEntityType] = useState('');

  const emptyValue = t('common:empty_value');
  const recordCount = events.length;

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { per_page: 100 };
      if (entityType) params.entity_type = entityType;
      const response = await getDataLifecycleEvents(params);
      setEvents(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const columns = useMemo(() => [
    {
      id: 'created_at',
      label: t('dataLifecycle:events.columns.when'),
      render: (row) => (
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontSize: 13,
            lineHeight: '20px',
            color: semantic.text.secondary,
            whiteSpace: 'nowrap',
          }}
        >
          {formatDate(row.created_at, emptyValue)}
        </Typography>
      ),
    },
    {
      id: 'event_type',
      label: t('dataLifecycle:events.columns.event'),
      render: (row) => (
        row.event_type
          ? (
            <StatusBadge
              status={row.event_type}
              category="generic"
              apiLabel={row.event_type}
              size="Sm"
              tone={eventTypeTone(row.event_type)}
            />
          )
          : emptyValue
      ),
    },
    {
      id: 'entity_type',
      label: t('dataLifecycle:recycle_bin.columns.entity'),
      render: (row) => (
        row.entity_type
          ? t(`dataLifecycle:entity_types.${row.entity_type}`, { defaultValue: row.entity_type })
          : emptyValue
      ),
    },
    {
      id: 'entity_id',
      label: t('common:id'),
      render: (row) => (
        row.entity_id != null && row.entity_id !== ''
          ? <Typography sx={monoSx}>{row.entity_id}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'actor_name',
      label: t('dataLifecycle:events.columns.actor'),
      render: (row) => row.actor_name || emptyValue,
    },
    {
      id: 'reason',
      label: t('common:reason'),
      render: (row) => (
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontSize: 13,
            lineHeight: '20px',
            color: semantic.text.secondary,
            maxWidth: 280,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          }}
        >
          {row.reason || emptyValue}
        </Typography>
      ),
    },
  ], [emptyValue, t]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('dataLifecycle:events.title')}
        subtitle={t('dataLifecycle:events.subtitle')}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('dataLifecycle:eventsHelp')}
      </Alert>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadEvents} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('dataLifecycle:events.load_failed')}
        </Alert>
      )}

      <FilterBar type="Simple">
        <FilterSelect
          label={t('dataLifecycle:recycle_bin.entity_type')}
          hideLabel
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          minWidth={200}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {ENTITY_FILTERS.map((type) => (
            <FilterSelectOption key={type} value={type}>
              {t(`dataLifecycle:entity_types.${type}`)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('dataLifecycle:events.events')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('dataLifecycle:events.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : !error ? (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={events}
            getRowKey={(row) => row.id}
            emptyMessage={t('dataLifecycle:events.no_events')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        ) : null}
      </SectionCard>
    </Box>
  );
}
